import React, { useState, useEffect } from 'react';
import { Card, Spin, Table, Input, Button, DatePicker, Radio, Select, Drawer, Space } from 'antd';
import {
  ShoppingCartOutlined,
  ReloadOutlined,
  SearchOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ReportService from '../../../services/ReportService';
import ReportCompanySelect from '../../../components/ReportCompanySelect';
import ReportBranchSelect from '../../../components/ReportBranchSelect';
import { getUpdatedMobileDateRange } from '../../../helpers/mobileDateRange';
import { useReportScope } from '../../../hooks/useReportScope';

const { RangePicker } = DatePicker;
const { Option } = Select;

const AverageTableConsumptionReports = () => {
  const [loading, setLoading] = useState(true);
  const [dateFilterType, setDateFilterType] = useState('quick'); // 'quick' or 'range'
  const [dateFilter, setDateFilter] = useState('today');
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [dateRange, setDateRange] = useState([dayjs().subtract(7, 'day'), dayjs()]);
  const [searchText, setSearchText] = useState('');
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [draftDateFilterType, setDraftDateFilterType] = useState('quick');
  const [draftDateFilter, setDraftDateFilter] = useState('today');
  const [draftSelectedDate, setDraftSelectedDate] = useState(dayjs());
  const [draftDateRange, setDraftDateRange] = useState([dayjs().subtract(7, 'day'), dayjs()]);
  const [draftSelectedBranch, setDraftSelectedBranch] = useState(null);
  const [draftAdminCompanyCode, setDraftAdminCompanyCode] = useState(null);

  const [averageData, setAverageData] = useState([]);
  const [averageDataLoading, setAverageDataLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const {
    token,
    allBranches,
    branchesLoading,
    companies,
    adminCompanyCode,
    setAdminCompanyCode,
    effectiveCompanyCode,
    needsCompanyPicker,
    getCompanyCodeForRequest,
  } = useReportScope();

  const getDateParams = (filterOverride = null, filterTypeOverride = null, dateRangeOverride = null, selectedDateOverride = null) => {
    const currentFilter = filterOverride !== null ? filterOverride : dateFilter;
    const currentFilterType = filterTypeOverride !== null ? filterTypeOverride : dateFilterType;
    const currentDateRange = dateRangeOverride !== null ? dateRangeOverride : dateRange;
    const currentSelectedDate = selectedDateOverride !== null ? selectedDateOverride : selectedDate;

    if (currentFilterType === 'range' && currentDateRange && currentDateRange[0] && currentDateRange[1]) {
      return {
        StartDate: currentDateRange[0].startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        EndDate: currentDateRange[1].add(1, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss')
      };
    }
    if (currentFilterType === 'quick' && currentFilter === 'custom') {
      return {
        StartDate: currentSelectedDate.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        EndDate: currentSelectedDate.add(1, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss')
      };
    }
    return {
      DateFilter: currentFilter
    };
  };

  const fetchAverageData = async (filterOverride = null, filterTypeOverride = null, dateRangeOverride = null, selectedDateOverride = null, branchCodeOverride = null, adminCompanyOverride = undefined) => {
    const resolvedBranchCode = branchCodeOverride !== null && branchCodeOverride !== undefined ? branchCodeOverride : (selectedBranch || null);
    const companyCode = getCompanyCodeForRequest(resolvedBranchCode, adminCompanyOverride);
    try {
      setAverageDataLoading(true);

      if (!companyCode) {
        console.error('CompanyCode is required');
        setAverageData([]);
        return;
      }

      const dateParams = getDateParams(filterOverride, filterTypeOverride, dateRangeOverride, selectedDateOverride);
      const response = await ReportService.sales.getBranchAverageTableConsumptionReport({
        token,
        CompanyCode: companyCode,
        BranchCode: resolvedBranchCode || undefined,
        ...dateParams
      });

      if (response.status === 200) {
        const data = Array.isArray(response.data) ? response.data : (response.data?.data || response.data || []);
        setAverageData(data);
      } else {
        setAverageData([]);
      }
    } catch (error) {
      console.error('Error fetching average table consumption:', error);
      setAverageData([]);
    } finally {
      setAverageDataLoading(false);
    }
  };

  useEffect(() => {
    if (branchesLoading) {
      return;
    }
    if (!effectiveCompanyCode) {
      setLoading(false);
      return;
    }
    const loadData = async () => {
      setLoading(true);
      try {
        await fetchAverageData();
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [effectiveCompanyCode, branchesLoading]);

  const handleFilterChange = (e) => {
    const newFilter = e.target.value;
    setDateFilter(newFilter);
    setDateFilterType('quick');
    if (newFilter !== 'custom') {
      fetchAverageData(newFilter, 'quick', null, null, selectedBranch);
    }
  };

  const handleCustomDateChange = (date) => {
    if (date) {
      setSelectedDate(date);
      setDateFilter('custom');
      fetchAverageData('custom', 'quick', null, date, selectedBranch);
    }
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (dates && dates[0] && dates[1]) {
      fetchAverageData(null, 'range', dates, null, selectedBranch);
    }
  };

  const handleBranchChange = (value) => {
    setSelectedBranch(value);
    fetchAverageData(dateFilter, dateFilterType, dateRange, selectedDate, value);
  };

  const openFilters = () => {
    setDraftDateFilterType(dateFilterType);
    setDraftDateFilter(dateFilter);
    setDraftSelectedDate(selectedDate);
    setDraftDateRange(dateRange);
    setDraftSelectedBranch(selectedBranch);
    setDraftAdminCompanyCode(adminCompanyCode);
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    setDateFilterType(draftDateFilterType);
    setDateFilter(draftDateFilter);
    setSelectedDate(draftSelectedDate);
    setDateRange(draftDateRange);
    setSelectedBranch(draftSelectedBranch);
    if (needsCompanyPicker) {
      setAdminCompanyCode(draftAdminCompanyCode);
    }
    setIsFilterOpen(false);

    if (draftDateFilterType === 'range') {
      fetchAverageData(null, 'range', draftDateRange, null, draftSelectedBranch, draftAdminCompanyCode);
    } else if (draftDateFilter === 'custom') {
      fetchAverageData('custom', 'quick', null, draftSelectedDate, draftSelectedBranch, draftAdminCompanyCode);
    } else {
      fetchAverageData(draftDateFilter, 'quick', null, null, draftSelectedBranch, draftAdminCompanyCode);
    }
  };

  const clearFilters = () => {
    const defaultDateFilterType = 'quick';
    const defaultDateFilter = 'today';
    const defaultSelectedDate = dayjs();
    const defaultDateRange = [dayjs().subtract(7, 'day'), dayjs()];
    const defaultSelectedBranch = null;

    setDraftDateFilterType(defaultDateFilterType);
    setDraftDateFilter(defaultDateFilter);
    setDraftSelectedDate(defaultSelectedDate);
    setDraftDateRange(defaultDateRange);
    setDraftSelectedBranch(defaultSelectedBranch);

    setDateFilterType(defaultDateFilterType);
    setDateFilter(defaultDateFilter);
    setSelectedDate(defaultSelectedDate);
    setDateRange(defaultDateRange);
    setSelectedBranch(defaultSelectedBranch);

    fetchAverageData(defaultDateFilter, defaultDateFilterType, null, null, null);
  };

  const handleMobileDraftRangeDateChange = (index, date) => {
    const nextRange = getUpdatedMobileDateRange(draftDateRange, index, date, 7);
    if (!nextRange) return;
    setDraftDateRange(nextRange);
  };

  const handleDateFilterTypeChange = (e) => {
    const newType = e.target.value;
    setDateFilterType(newType);
    if (newType === 'range') {
      fetchAverageData(null, 'range', dateRange, null);
    } else {
      fetchAverageData(dateFilter, 'quick', null, selectedDate);
    }
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const calculateTotalMasa = (data) => {
    return data.reduce((sum, row) => sum + parseFloat(row.ToplamMasaSayisi || 0), 0);
  };

  const calculateTotalKazanc = (data) => {
    return data.reduce((sum, row) => sum + parseFloat(row.ToplamKazanc || 0), 0);
  };

  const calculateAverageOrtalama = (data) => {
    const totalMasa = calculateTotalMasa(data);
    const totalKazanc = calculateTotalKazanc(data);
    return totalMasa > 0 ? totalKazanc / totalMasa : 0;
  };

  const averageColumns = [
    {
      title: <span className={isMobile ? 'text-xs whitespace-nowrap' : 'text-base'}>{isMobile ? 'Şube' : 'Şube Adı'}</span>,
      dataIndex: 'SubeAdi',
      key: 'SubeAdi',
      width: isMobile ? '28%' : '25%',
      render: (text) => <span className={`${isMobile ? 'text-sm' : 'text-base'} font-semibold`}>{text}</span>,
      ellipsis: isMobile
    },
    {
      title: <span className={isMobile ? 'text-xs whitespace-nowrap' : 'text-base'}>{isMobile ? 'Masa' : 'Toplam Masa Sayısı'}</span>,
      dataIndex: 'ToplamMasaSayisi',
      key: 'ToplamMasaSayisi',
      width: isMobile ? '20%' : '20%',
      render: (value) => (
        <span className={`${isMobile ? 'text-sm' : 'text-base'} font-semibold text-blue-600`}>
          {formatNumber(value || 0)}
        </span>
      ),
      sorter: isMobile ? false : (a, b) => (a.ToplamMasaSayisi || 0) - (b.ToplamMasaSayisi || 0),
    },
    {
      title: <span className={isMobile ? 'text-xs whitespace-nowrap' : 'text-base'}>{isMobile ? 'Kazanç' : 'Toplam Kazanç'}</span>,
      dataIndex: 'ToplamKazanc',
      key: 'ToplamKazanc',
      width: isMobile ? '24%' : '20%',
      render: (value) => (
        <span className={`${isMobile ? 'text-sm' : 'text-base'} font-semibold text-green-600`}>
          {formatNumber(value || 0)} ₺
        </span>
      ),
      sorter: isMobile ? false : (a, b) => (a.ToplamKazanc || 0) - (b.ToplamKazanc || 0),
    },
    {
      title: <span className={isMobile ? 'text-xs whitespace-nowrap' : 'text-base'}>{isMobile ? 'Ort. Tük.' : 'Ortalama Masa Başına Tüketim'}</span>,
      dataIndex: 'OrtalamaMasaBasiTuketim',
      key: 'OrtalamaMasaBasiTuketim',
      width: isMobile ? '28%' : '20%',
      render: (value) => (
        <span className={`${isMobile ? 'text-sm' : 'text-base'} font-semibold text-purple-600`}>
          {formatNumber(value || 0)} ₺
        </span>
      ),
      sorter: isMobile ? false : (a, b) => (a.OrtalamaMasaBasiTuketim || 0) - (b.OrtalamaMasaBasiTuketim || 0),
    },
  ];

  const filterData = (data) => {
    if (!searchText) return data;
    const needle = searchText.toLowerCase();
    return data.filter(row =>
      String(row.SubeAdi || '').toLowerCase().includes(needle) ||
      String(row.SubeKodu || '').toLowerCase().includes(needle)
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-50 via-white to-slate-50 border border-slate-200/60 shadow-lg backdrop-blur-sm mb-4">
        <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 via-emerald-500/5 to-teal-500/5"></div>
        <div className="relative px-3 py-2 sm:px-4 sm:py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg sm:rounded-xl shadow-lg">
              <ShoppingCartOutlined className="text-white text-lg sm:text-xl" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent leading-tight">
                Masa Ortalama Tüketim
              </h1>
              <p className="text-slate-500 text-xs sm:text-sm font-medium">
                Şube bazlı kapanmış ve açık masalar için ortalama tüketim raporları
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchAverageData(dateFilter, dateFilterType, dateRange, selectedDate, selectedBranch)}
              loading={averageDataLoading}
            >
              Yenile
            </Button>
            <Button icon={<FilterOutlined />} onClick={openFilters}>Filtreler</Button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <Input
          allowClear
          placeholder="Şube ara..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="large"
          className="flex-1 rounded-lg"
        />
      </div>

      {/* Average Table Consumption Report */}
      <Card
        title={
          <div className="flex items-center gap-2">
            <ShoppingCartOutlined className="text-green-500" />
            <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold`}>
              Masa Ortalama Tüketim
            </h2>
          </div>
        }
        bordered={false}
        className="shadow-lg rounded-2xl border border-gray-100 bg-white"
        styles={{ body: { padding: isMobile ? 12 : 18 } }}
      >
        <Table
          columns={averageColumns}
          dataSource={filterData(averageData)}
          loading={averageDataLoading}
          rowKey="SubeKodu"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Toplam ${total} şube`,
          }}
          size={isMobile ? 'small' : 'middle'}
          summary={(pageData) => {
            const totalMasa = calculateTotalMasa(pageData);
            const totalKazanc = calculateTotalKazanc(pageData);
            const averageOrtalama = calculateAverageOrtalama(pageData);
            return (
              <Table.Summary fixed>
                <Table.Summary.Row className="bg-gray-50">
                  <Table.Summary.Cell index={0}>
                    <span className="font-bold text-base">Genel Toplam</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <span className="font-bold text-base text-blue-600">{formatNumber(totalMasa)}</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    <span className="font-bold text-base text-green-600">{formatNumber(totalKazanc)} ₺</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>
                    <span className="font-bold text-base text-purple-600">{formatNumber(averageOrtalama)} ₺</span>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>

      <Drawer title="Filtreler" placement="right" open={isFilterOpen} onClose={() => setIsFilterOpen(false)} width={isMobile ? 280 : 360}>
        <Space direction="vertical" size="middle" className="w-full">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Filtre Tipi</span>
            <Radio.Group
              value={draftDateFilterType}
              onChange={(e) => setDraftDateFilterType(e.target.value)}
              className="w-full"
            >
              <Radio.Button value="quick" className="w-1/2 text-center">Gün</Radio.Button>
              <Radio.Button value="range" className="w-1/2 text-center">Tarih Aralığı</Radio.Button>
            </Radio.Group>
          </div>

          {draftDateFilterType === 'quick' ? (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-slate-500">Gün Seçimi</span>
                <div className="flex flex-col gap-2">
                  {[
                    { label: 'Bugün', value: 'today' },
                    { label: 'Dün', value: 'yesterday' },
                    { label: 'Gün Seçin', value: 'custom' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setDraftDateFilter(option.value)}
                      className={`
                        px-3 py-2 text-sm font-semibold transition-all duration-300 ease-out rounded-lg
                        ${draftDateFilter === option.value
                          ? 'bg-gradient-to-r from-rose-500 to-red-600 text-white shadow-lg'
                          : 'text-slate-600 hover:text-slate-800 hover:bg-gray-100 border border-gray-200'
                        }
                      `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              {draftDateFilter === 'custom' && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-slate-500">Özel Gün</span>
                  <DatePicker
                    value={draftSelectedDate}
                    onChange={(date) => setDraftSelectedDate(date)}
                    format="DD.MM.YYYY"
                    className="w-full"
                    placeholder="Gün seçin"
                    placement="bottomLeft"
                    getPopupContainer={(trigger) => trigger?.parentElement || document.body}
                    popupClassName="mobile-drawer-date-popup"
                  />
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-slate-500">Tarih Aralığı</span>
              {isMobile ? (
                <div className="flex items-center gap-1 w-full">
                  <DatePicker
                    value={draftDateRange?.[0]}
                    onChange={(date) => handleMobileDraftRangeDateChange(0, date)}
                    format="DD.MM.YYYY"
                    placeholder="Başlangıç"
                    className="w-full"
                    size="small"
                    allowClear={false}
                    placement="bottomLeft"
                    getPopupContainer={(trigger) => trigger?.parentElement || document.body}
                    popupClassName="mobile-drawer-date-popup"
                  />
                  <span className="text-slate-400 text-xs">-</span>
                  <DatePicker
                    value={draftDateRange?.[1]}
                    onChange={(date) => handleMobileDraftRangeDateChange(1, date)}
                    format="DD.MM.YYYY"
                    placeholder="Bitiş"
                    className="w-full"
                    size="small"
                    allowClear={false}
                    placement="bottomLeft"
                    getPopupContainer={(trigger) => trigger?.parentElement || document.body}
                    popupClassName="mobile-drawer-date-popup"
                  />
                </div>
              ) : (
                <RangePicker
                  value={draftDateRange}
                  onChange={(dates) => setDraftDateRange(dates || [])}
                  format="DD.MM.YYYY"
                  className="w-full"
                  placement="bottomLeft"
                  getPopupContainer={(trigger) => trigger?.parentElement || document.body}
                />
              )}
            </div>
          )}

          <ReportCompanySelect
            companies={companies}
            value={draftAdminCompanyCode}
            onChange={(value) => {
              setDraftAdminCompanyCode(value);
              setDraftSelectedBranch(null);
            }}
            loading={branchesLoading}
          />

          <ReportBranchSelect
            allBranches={allBranches}
            needsCompanyPicker={needsCompanyPicker}
            draftAdminCompanyCode={draftAdminCompanyCode}
            adminCompanyCode={adminCompanyCode}
            effectiveCompanyCode={effectiveCompanyCode}
            value={draftSelectedBranch}
            onChange={setDraftSelectedBranch}
            loading={branchesLoading}
          />

          <div className="flex items-center justify-between gap-2 pt-2">
            <Button onClick={clearFilters}>Temizle</Button>
            <Button type="primary" danger ghost={false} onClick={applyFilters} className="!bg-rose-600 !border-rose-600 !text-white hover:!bg-rose-700 hover:!border-rose-700">Uygula</Button>
          </div>
        </Space>
      </Drawer>
    </div>
  );
};

export default AverageTableConsumptionReports;

